import type { StrategyWithToken } from "@/types/strategy";
import type { TokenReveal } from "./useStrategyActions";

export interface CreateReveal extends TokenReveal {
  /** Always false since bias retired (2026-08-28) — every surviving
   * strategy generates its own signals and shows an info note instead of
   * webhook URLs. Kept so the dialog's two shapes stay explicit. */
  webhook: boolean;
  /** Which internal engine the note names — only read when `webhook` is
   * false. */
  engineLabel?: string;
  /** The engine's market-data venue named in the note (Fyers for
   * supertrend/ut_bot, Zerodha for bb_bottom_buy). Only read when
   * `webhook` is false. */
  engineSource?: string;
  entryUrl?: string;
  exitUrl?: string;
}

/** STPIVOT: one strategy per index, Dhan, PAPER first (docs/stpivot.md). */
function stpivotReveal(s: StrategyWithToken): CreateReveal {
  const index = s.strategy_type === "stpivot_nifty" ? "NIFTY" : "SENSEX";
  return {
    token: s.access_token,
    webhook: false,
    engineLabel: `STPIVOT ${index}`,
    engineSource: "Dhan",
    title: `Strategy created - ${s.name}`,
    description: `STPIVOT ${index} reads the 5-minute candles from Dhan and trades through Dhan, in PAPER first — no webhook or TradingView alert to set up. SuperTrend (7,3) with the previous session's pivot R1 and S1; any number of trades a day on every weekday. Dhan must be connected on the Broker page.`,
  };
}

/** Manual trading: no signals of its own, one strategy per broker (docs/manual_trading.md). */
function manualReveal(s: StrategyWithToken): CreateReveal {
  const venue = s.broker === "fyers" ? "Fyers" : "Zerodha";
  return {
    token: s.access_token,
    webhook: false,
    engineLabel: "Manual trading",
    engineSource: venue,
    title: `Strategy created - ${s.name}`,
    description: `Manual trading has no signals of its own: it trades only when the chart sends an entry or an exit, on ${venue}, in PAPER first. The chart picks this strategy by its broker, so keep exactly one Manual trading strategy per broker. Lots come from the chart; no webhook or TradingView alert to set up.`,
  };
}

export function makeCreateReveal(s: StrategyWithToken): CreateReveal {
  if (s.strategy_type === "ema_trend") {
    return {
      token: s.access_token,
      webhook: false,
      engineLabel: "5/50 EMA Trend",
      // Zerodha, deliberately: this engine READS Kite index candles but
      // PLACES its orders on Fyers, so both logins have to be current. Naming
      // only the order venue here would hide half of what it needs.
      engineSource: "Zerodha",
      title: `Strategy created - ${s.name}`,
      description:
        "5/50 EMA Trend generates its own signals from Zerodha index data and places its orders on Fyers — no webhook or TradingView alert to set up. Both logins must be current for it to trade.",
    };
  }
  if (s.strategy_type === "poi1") {
    return {
      token: s.access_token,
      webhook: false,
      engineLabel: "POI-1",
      engineSource: "Zerodha",
      title: `Strategy created - ${s.name}`,
      description:
        "POI-1 reads the opening candle from Zerodha index data and places its orders on Zerodha — no webhook or TradingView alert to set up. One trade a day: Thursday SENSEX, every other weekday NIFTY.",
    };
  }
  if (s.strategy_type === "failed_breakout") {
    return {
      token: s.access_token,
      webhook: false,
      engineLabel: "Failed Breakout",
      engineSource: "Dhan",
      title: `Strategy created - ${s.name}`,
      description:
        "Failed Breakout reads the first 15-minute candle and the 5-minute candles from Dhan and trades through Dhan, in PAPER first — no webhook or TradingView alert to set up. One trade a day: NIFTY Monday, Tuesday and Friday, SENSEX Wednesday and Thursday. Dhan must be connected on the Broker page.",
    };
  }
  if (s.strategy_type === "stpivot_nifty" || s.strategy_type === "stpivot_sensex") {
    return stpivotReveal(s);
  }
  if (s.strategy_type === "manual") return manualReveal(s);
  // Only reachable for a retired type (bias, bb_bottom_buy), which the
  // create dialog no longer offers.
  return {
    token: s.access_token,
    webhook: false,
    engineLabel: "Retired",
    engineSource: "Zerodha",
    title: `Strategy created - ${s.name}`,
    description: "This strategy type is retired — no engine will run it.",
  };
}
